import { useCallback, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { IS_DEV_RESEARCH_ENV } from "constants/app.constants";
import {
  FORECAST_FOLDER_ID_TO_FORECAST_SOURCE_MAP,
  MCDANIEL_SEARCH_FOLDER_NAME,
  NOVI_FORECAST_SOURCE,
  WORKING_FORECAST_SOURCE,
  isForecastTrumpingSource
} from "constants/settings.constants";
import { RootState } from "store/rootReducer";
import {
  selectCheckedGlobalForecasts,
  selectUseMcDanielForecasts,
  selectUseNoviForecasts,
  selectUseWorkingForecasts,
  useForecastStore
} from "stores/forecast";
import { buildForecastTrumpingSettings } from "utils/data-table";
import { qualifyPropertyWithForecast } from "utils/data-table/getPropertiesWithForecast";

import { WellDetailCardData } from "api/data";

import { ForecastTrumpParamsType } from "models/schemas/data.schema";

import { useNoviAccess } from "components/arps/hooks/useHasNoviAccess";
import { DEFAULT_RESCAT } from "components/groupBy/groupByStore";
import { getForecastSourceFromSelection } from "components/groupBy/helpers";
import { useUserDefaults } from "components/user/hooks";

export interface WellCardForecastQualification {
  /** Request field names, forecast-backed ones qualified for the panel's source. */
  fields: string[];
  /** Null when the list has no forecast-backed field, so it can key a query. */
  forecastSource: Nullable<string>;
  forecastTrumpingSettings: Maybe<ForecastTrumpParamsType>;
  /** Re-keys a response by the base names the card's blocks read. */
  toBaseNames: (data: WellDetailCardData) => WellDetailCardData;
}

// The card has no Forecast Source dropdown, so this repeats the one the dropdown applies when the
// panel names no source (ForecastSourceSelect.tsx: saved default through the folder-id map, then
// EVA Research when the result is not offered to this org/env or is unmapped).
function resolveSavedDefaultSource(
  savedFolderId: Maybe<string>,
  hasNoviAccess: boolean
): string {
  const source = savedFolderId
    ? FORECAST_FOLDER_ID_TO_FORECAST_SOURCE_MAP[savedFolderId]
    : undefined;
  if (
    !source ||
    (source === NOVI_FORECAST_SOURCE && !hasNoviAccess) ||
    (source === WORKING_FORECAST_SOURCE && !IS_DEV_RESEARCH_ENV)
  ) {
    return MCDANIEL_SEARCH_FOLDER_NAME;
  }
  return source;
}

/**
 * Makes the card's forecast-backed fields follow the project Forecasts panel the
 * way the chart axis selector and the data table do.
 *
 * Novi entitlement only narrows the source; it is not waited on. A failed
 * entitlement fetch stays non-authoritative for good, and the card must still
 * render, so until it resolves a Novi tick is read as not ticked.
 */
export function useWellCardForecastQualification(
  baseFields: readonly string[]
): WellCardForecastQualification {
  const forecastMode = useSelector((state: RootState) => state.app.forecastMode);
  const { hasNoviAccess } = useNoviAccess();
  const useMcDanielForecasts = useForecastStore(selectUseMcDanielForecasts);
  const useNoviForecasts = useForecastStore(selectUseNoviForecasts);
  const useWorkingForecasts = useForecastStore(selectUseWorkingForecasts);
  const checkedGlobalForecasts = useForecastStore(selectCheckedGlobalForecasts);
  const specialForecastFolders = useSelector(
    (state: RootState) => state.app.userForecastSettings.specialForecastFolders,
    shallowEqual
  );
  const forecastReserveCategory = useSelector(
    (state: RootState) => state.app.userForecastSettings.reserveCategory
  );
  const syncWells = useSelector((state: RootState) => state.app.syncWells);
  const savedFolderId = useUserDefaults()?.forecastSourceSetting?.forecastFolderId;

  const panelSource = getForecastSourceFromSelection({
    forecastMode,
    useMcDanielForecasts,
    useNoviForecasts: useNoviForecasts && hasNoviAccess,
    useWorkingForecasts
  });
  const source = panelSource ?? resolveSavedDefaultSource(savedFolderId, hasNoviAccess);
  const syncJob = syncWells?.isSync ? syncWells.schema : undefined;
  const syncJobRescat = syncWells?.isSync ? syncWells.forecastRescat : undefined;

  const { fields, nameMap } = useMemo(() => {
    const map = new Map<string, string>();
    const qualified = baseFields.map((base) => {
      const name = qualifyPropertyWithForecast(base, source, DEFAULT_RESCAT);
      if (name !== base) {
        map.set(name, base);
      }
      return name;
    });
    return { fields: qualified, nameMap: map };
  }, [baseFields, source]);

  const hasForecastFields = nameMap.size > 0;

  const forecastTrumpingSettings = useMemo(() => {
    if (!hasForecastFields || !isForecastTrumpingSource(source)) {
      return undefined;
    }
    const settings = buildForecastTrumpingSettings(
      checkedGlobalForecasts,
      specialForecastFolders,
      syncJob,
      syncJobRescat,
      forecastReserveCategory
    );
    return settings.userForecastFolderId || specialForecastFolders.length > 0
      ? settings
      : undefined;
  }, [
    hasForecastFields,
    source,
    checkedGlobalForecasts,
    specialForecastFolders,
    syncJob,
    syncJobRescat,
    forecastReserveCategory
  ]);

  const toBaseNames = useCallback(
    (data: WellDetailCardData): WellDetailCardData => {
      if (!data || nameMap.size === 0) {
        return data;
      }
      return Object.fromEntries(
        Object.entries(data).map(([key, value]) => [nameMap.get(key) ?? key, value])
      );
    },
    [nameMap]
  );

  return {
    fields,
    forecastSource: hasForecastFields ? source : null,
    forecastTrumpingSettings,
    toBaseNames
  };
}
